import { IonAvatar, IonBackButton, IonButton, IonButtons, IonContent, IonHeader, IonIcon, IonItem, IonLabel, IonList, IonPage, IonProgressBar, IonSpinner, IonTitle, IonToolbar, useIonAlert, NavContext } from '@ionic/react';
import React, { memo, useContext } from "react";
import { checkmarkOutline, timeOutline } from 'ionicons/icons';
import OCAuth from '../client/OCAuth';
import useUX from '../utils/ux';
import { RouteComponentProps } from 'react-router-dom';
import { useAcceptDeviceUserInviteMutation, useGetDeviceUserInviteQuery } from '../query/deviceUserInviteQuery';
import ModalFooter from '../components/ModalFooter';
import footerStyles from '../components/ModalFooter.module.css';
import styles from './AccountInvite.module.css';
import { format } from 'date-fns';
import { useCurrentUserQuery } from '../query/currentUserQuery';
import { useGatewayConnectionStateQuery } from '../query/gatewayConnectionStateQuery';
import { useCommonTranslation, usePageTranslation } from '../hooks/useTranslation';

interface AccountInviteProps extends RouteComponentProps<{
    inviteToken: string;
}> { }

const AccountInvite: React.FC<AccountInviteProps> = ({ match }) => {
    const { inviteToken } = match.params;
    const { goBack } = useContext(NavContext);
    const currentUserQuery = useCurrentUserQuery();
    const gatewayConnectionStateQuery = useGatewayConnectionStateQuery();
    const user = currentUserQuery.data;
    const [presentAlert] = useIonAlert();
    const { presentResult } = useUX();
    const { t } = usePageTranslation();
    const { t: tCommon } = useCommonTranslation();
    const acceptInviteMutation = useAcceptDeviceUserInviteMutation();
    const inviteQuery = useGetDeviceUserInviteQuery(inviteToken);

    const invite = inviteQuery.data;
    // The gateway answers 404 for unknown and expired tokens alike.
    const expired = inviteQuery.isError;
    const accepted = acceptInviteMutation.isSuccess;

    const deviceName = invite ? (invite.device.description || invite.device.deviceId) : '';
    const inviterFirstName = invite ? (invite.invitingUser.name?.split(' ')[0] || invite.invitingUser.email) : '';

    const login = async () => {
        await OCAuth.login();
    };

    const acceptInvitation = async () => {
        await presentAlert({
            header: t('account_invite.accept_alert.header'),
            message: t('account_invite.accept_alert.message', { device: deviceName }),
            buttons: [
                {
                    text: tCommon('buttons.cancel'),
                    role: 'cancel'
                },
                {
                    text: t('account_invite.accept_alert.accept'),
                    handler: async () => {
                        presentResult(async () => {
                            const result = await acceptInviteMutation.mutateAsync(inviteToken);

                            goBack('/devices/' + result.deviceId);
                        }, t('account_invite.accepted_toast'));
                    }
                }
            ]
        });
    };

    const formatDate = (dateStr: string) => {
        try {
            return format(new Date(dateStr), 'PPp');
        } catch (e) {
            return dateStr;
        }
    };

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonBackButton defaultHref="/account" />
                    </IonButtons>
                    <IonTitle>{t('account_invite.title')}</IonTitle>
                    {!!(gatewayConnectionStateQuery.isConnecting || currentUserQuery.isLoading || inviteQuery.isLoading) && <IonProgressBar type="indeterminate" />}
                </IonToolbar>
            </IonHeader>
            <IonContent color="light" fullscreen>
                <div className="oc-page-container">
                <div className={`${styles.hero} ${expired ? styles.heroExpired : ''}`}>
                    <img
                        className={`${styles.heroImage} ${expired ? styles.heroImageExpired : ''}`}
                        src="/assets/device/onlycat-device.png"
                        alt=""
                    />
                    {inviteQuery.isLoading ? (
                        <div className={styles.heroLoading}>
                            <IonSpinner />
                            <span>{t('account_invite.loading')}</span>
                        </div>
                    ) : (
                        <>
                            <h1 className={styles.heroTitle}>
                                {expired
                                    ? t('account_invite.headline_expired')
                                    : t('account_invite.headline_invited', { name: inviterFirstName, device: deviceName })}
                            </h1>
                            <p className={styles.heroSubtitle}>
                                {expired
                                    ? t('account_invite.subtitle_expired')
                                    : user
                                        ? t('account_invite.subtitle_signed_in')
                                        : t('account_invite.subtitle_signed_out')}
                            </p>
                        </>
                    )}
                </div>

                {invite && (
                    <div className={styles.wrapper}>
                        <IonList className={styles.listCard}>
                            <IonItem>
                                <img slot="start" className={styles.rowDeviceImage} src="/assets/device/onlycat-device.png" alt="" />
                                <IonLabel>
                                    <p className={styles.factLabel}>{t('account_invite.fact_device')}</p>
                                    <h2>{deviceName}</h2>
                                    {invite.device.description && <p>{invite.device.deviceId}</p>}
                                </IonLabel>
                            </IonItem>
                            <IonItem>
                                <IonAvatar slot="start" className={styles.rowAvatar}>
                                    <img
                                        src={invite.invitingUser.avatarUrl || '/assets/account/default-avatar.svg'}
                                        alt={invite.invitingUser.name || t('account_invite.fact_invited_by')}
                                        referrerPolicy="no-referrer"
                                    />
                                </IonAvatar>
                                <IonLabel>
                                    <p className={styles.factLabel}>{t('account_invite.fact_invited_by')}</p>
                                    <h2>{invite.invitingUser.name || invite.invitingUser.email}</h2>
                                    {invite.invitingUser.name && invite.invitingUser.email && <p>{invite.invitingUser.email}</p>}
                                </IonLabel>
                            </IonItem>
                            {user && (
                                <IonItem>
                                    <IonAvatar slot="start" className={styles.rowAvatar}>
                                        <img
                                            src={user.avatarUrl ?? '/assets/account/default-avatar.svg'}
                                            alt={user.name ?? ''}
                                            referrerPolicy="no-referrer"
                                        />
                                    </IonAvatar>
                                    <IonLabel>
                                        <p className={styles.factLabel}>{t('account_invite.fact_your_account')}</p>
                                        <h2>{user.name || user.email}</h2>
                                        {user.name && user.email && <p>{user.email}</p>}
                                    </IonLabel>
                                </IonItem>
                            )}
                            <IonItem>
                                <span slot="start" className={styles.iconCircle} aria-hidden="true">
                                    <IonIcon icon={timeOutline} />
                                </span>
                                <IonLabel>
                                    <p className={styles.factLabel}>{t('account_invite.fact_expires')}</p>
                                    <h2>{formatDate(invite.expiresAt)}</h2>
                                </IonLabel>
                            </IonItem>
                        </IonList>
                    </div>
                )}
                </div>
            </IonContent>
            <ModalFooter>
                {expired ? (
                    <IonButton
                        expand="block"
                        fill="outline"
                        className={`${footerStyles.primaryButton} ${styles.expiredButton}`}
                        onClick={() => goBack('/devices')}
                    >
                        {t('account_invite.back_to_devices')}
                    </IonButton>
                ) : accepted ? (
                    <IonButton expand="block" className={`${footerStyles.primaryButton} ${styles.grantedButton}`}>
                        <IonIcon slot="start" icon={checkmarkOutline} />
                        {t('account_invite.access_granted')}
                    </IonButton>
                ) : (
                    <>
                        {user ? (
                            <IonButton
                                expand="block"
                                className={footerStyles.primaryButton}
                                onClick={acceptInvitation}
                                disabled={!acceptInviteMutation.isIdle || !invite}
                            >
                                {t('account_invite.accept')}
                            </IonButton>
                        ) : (
                            <IonButton expand="block" className={footerStyles.primaryButton} onClick={login}>
                                {t('account_invite.sign_in')}
                            </IonButton>
                        )}
                        <IonButton
                            expand="block"
                            fill="clear"
                            color="medium"
                            className={footerStyles.ghostButton}
                            onClick={() => goBack('/account')}
                        >
                            {t('account_invite.not_now')}
                        </IonButton>
                    </>
                )}
            </ModalFooter>
        </IonPage >
    );
};

export default memo(AccountInvite);
